/**
 * 乖離率バッジ (spec AC-DFD-009 可変化分。文言は「想定比 +X.X%（至近{W}コマ
 * 平均）」、design doc AC-17 + demand-trend-window-variable design D3)。
 *
 * 集計窓 W はバッジ文言内にインライン number input として同居する (design
 * `.dft-trend-bar` の「至近<input>コマ平均)」構造。design/js/i18n.js が同じ
 * 理由 — input を挟むため辞書を「（至近」/「コマ平均）」の2 text node に
 * 分割している — で本コンポーネントも `positivePrefix`/`negativePrefix` +
 * `windowSuffix` の 2-key 構成にする)。W の再計算 (avgRatio・破線) は backend
 * RPC (PreviewTrendReflection) が担う — frontend は結果を表示するだけ。
 *
 * neutral 分岐は持たず、プラス/マイナスの2値のみで表示する (grill Q4/Q11/Q19 =
 * AC-28)。符号は avgRatio と 1 の大小のみで決まる (avgRatio >= 1 → プラス、
 * avgRatio < 1 → マイナス)。表示丸めで 0.0% になっても算術符号は維持する。
 *
 * avgRatio が算出できない (2件未満の実績コマ、AC-013/030) 場合は呼び出し元が
 * バッジ自体を非表示にする (Toolbar 側の hasTrendReflect && avgRatio !== null gate)。
 *
 * a11y: 色だけでなく "+" / "−" 符号で方向を区別する (TC-N-04)。
 * role="status" で aria-live に対応。
 */
import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { Badge, Input } from '@enechain/ecloud-designsystem'

import { HintTooltip } from '~/components/common/HintTooltip'
import {
  TREND_WINDOW_KOMA_MAX,
  TREND_WINDOW_KOMA_MIN,
  clampTrendWindowKoma,
} from '~/pages/DemandForecastDaily/models/trendWindowStorage'

type Props = {
  /** backend が算出した乖離率 (実績/計画)。呼び出し元は null でないことを保証する。 */
  avgRatio: number
  /** 集計窓 W (コマ数、AC-DFD-009a: 1〜24)。 */
  windowKoma: number
  /** W 変更ハンドラ (クランプ・端末保存・再計算 RPC の呼び出しは呼び出し元が持つ)。 readOnly=true 時は未使用。 */
  onWindowKomaChange?: (koma: number) => void
  /**
   * true の時 W を編集不能な文字表示にする (TrendReflectDialog: 反映確認中に
   * 集計窓を変えられると before/after 合計との整合が崩れるため、確認ダイアログ
   * では表示専用にする)。既定 false (Toolbar の通常表示は編集可)。
   */
  readOnly?: boolean
}

/**
 * 乖離率 (avgRatio) から spec AC-009 の符号付きパーセンテージ (algebraic、
 * neutral 分岐なし = AC-28) を導出する。
 * avgRatio=1.025 → { status: 'positive', pct: '2.5' }
 * avgRatio=0.975 → { status: 'negative', pct: '2.5' }
 */
function deviationSign(avgRatio: number): {
  status: 'positive' | 'negative'
  pct: string
} {
  const deviation = (avgRatio - 1) * 100
  const pct = Math.abs(deviation).toFixed(1)
  return { status: avgRatio >= 1 ? 'positive' : 'negative', pct }
}

export const DeviationBadge = ({
  avgRatio,
  windowKoma,
  onWindowKomaChange,
  readOnly = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { status, pct } = deviationSign(avgRatio)
  const prefix = t(
    status === 'positive'
      ? 'page:demandForecastDaily.deviationBadge.positivePrefix'
      : 'page:demandForecastDaily.deviationBadge.negativePrefix',
    { pct },
  )
  const suffix = t('page:demandForecastDaily.deviationBadge.windowSuffix')
  const ariaLabel = `${prefix}${windowKoma}${suffix}`

  // 入力途中の1文字ごとに再計算しないため、確定 (blur / Enter) までは local
  // draft で保持する (design `.dft-trend-koma` の change イベント相当)。
  const [draft, setDraft] = useState(String(windowKoma))
  // windowKoma が外部要因 (localStorage 初期化の非同期解決・他タブでの変更等)
  // で変わったら draft を追従させる (未編集中のみ — 編集中に上書きしない
  // ことは commit() が呼ばれるまで windowKoma 自体が変わらないため両立する)。
  useEffect(() => {
    setDraft(String(windowKoma))
  }, [windowKoma])

  const commit = (): void => {
    const raw = draft.trim()
    if (raw === '') {
      // 空/非数は直前値へ戻す (design 準拠: 入力ミスで集計窓が黙って変わらない)。
      setDraft(String(windowKoma))
      return
    }
    const n = Math.round(Number(raw))
    if (!Number.isFinite(n)) {
      setDraft(String(windowKoma))
      return
    }
    const clamped = clampTrendWindowKoma(n)
    setDraft(String(clamped))
    if (clamped !== windowKoma) onWindowKomaChange?.(clamped)
  }

  return (
    <Badge aria-label={ariaLabel} role="status" status={status}>
      {prefix}
      {readOnly ? (
        windowKoma
      ) : (
        // 入力の補足 (集計コマ数の範囲) は DS Tooltip で出す (UI 辞書 tooltip.md)。
        // native title は Tooltip 非対応環境向けの保険として残す (HintTooltip の JSDoc)。
        // content は常に同じ文字列なので、 focus 中の入力が作り直されることはない。
        <HintTooltip
          content={t(
            'page:demandForecastDaily.deviationBadge.windowInputTitle',
          )}
        >
          <Input
            width="52px"
            textAlign="right"
            aria-label={t(
              'page:demandForecastDaily.deviationBadge.windowInputAriaLabel',
            )}
            marginX={1}
            max={TREND_WINDOW_KOMA_MAX}
            min={TREND_WINDOW_KOMA_MIN}
            onBlur={commit}
            onChange={(e) => {
              setDraft(e.target.value)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.currentTarget.blur()
              }
            }}
            size="sm"
            step={1}
            title={t(
              'page:demandForecastDaily.deviationBadge.windowInputTitle',
            )}
            type="number"
            value={draft}
          />
        </HintTooltip>
      )}
      {suffix}
    </Badge>
  )
}
